import PoolDeviceOnOffable from "./base-onoffable";
import { DisplayProcessingLogics, PoolDeviceCriticalDesiredActual } from "../Types";
import { IOnoffable, PoolDeviceInterface } from "../Interfaces";
import Locale from "../../utils/Locale";
import { DeviceTypes } from "../../config/documents/HardwareConfig";
import * as Config from "../../config";
import { getIncrementNames } from "../../utils/Dictionary";
import { getLightInfo } from "../../catalog";
import { ValueAndUnit } from "../../types";
import { separator } from "../../types";


export enum criticalFieldIdentifiers {
    Brightness = "Brightness",
    LightName = "LightName",
    OnOff = "OnOff",
    Speed = "Speed",
    Twinkle = "Twinkle",
}

export default class PoolDeviceLight extends PoolDeviceOnOffable implements PoolDeviceInterface, IOnoffable {
    static DeviceType = DeviceTypes.LIGHT;
    static Icon = "sun";
    OnOffIdentifier = criticalFieldIdentifiers.OnOff;
    static PickerTitle = Locale.gettext("Light");

    formatDeviceStatusCustom(DisplayProcessingLogic: DisplayProcessingLogics, value: any): ValueAndUnit {
        switch(DisplayProcessingLogic) {
        case DisplayProcessingLogics.LIGHT_NAME:
            const colors = Config.getLightColors(this.deviceConfig);
            if (colors) {
                const lightName = this.getDeviceState(PoolsideDaemonDeviceStateNames.LightName)?.value;
                const currVal = colors.find(clr => clr.uuid === lightName);
                return { value: currVal?.name || Locale.gettext("Custom") };
            }
            return value;
        case DisplayProcessingLogics.TWINKLE:
            const twinkleIncrements = this.getDeviceState(PoolsideDaemonDeviceStateNames.TwinkleIncrements)?.value;
            if (twinkleIncrements) {
                const twinkleIncrementsDecoded = (JSON.parse(twinkleIncrements ?? "[]") || []) as { value: string, description: string }[];
                const twinkleIncrement = twinkleIncrementsDecoded.find(item => item.value == value);
                if (twinkleIncrement) {
                    const increments = getIncrementNames();
                    const translated = increments[ twinkleIncrement.description ];
                    return { value: translated ?? value };
                }
                return { value };
            }
            return { value };
        default: return super.formatDeviceStatusCustom(DisplayProcessingLogic, value);
        }
    }

    getDisplayNameTranslated(displayName:string) {
        switch (displayName) {
        case "Light Name": return Locale.gettext("Light Name");
        default: return super.getDisplayNameTranslated(displayName);
        }
    }

    getCriticalFieldSummaryCustom(): string {
        const colorAndShowLibrary = getLightInfo(this.deviceConfig?.HardwareDevice?.LightSettings)?.colorAndShowLibrary;
        const supportsTwinkleSpeedAndDirection = colorAndShowLibrary?.supportsTwinkleSpeedAndDirection;
        const supportsSpeed = colorAndShowLibrary?.supportsSpeed;
        const supportsBrightness = colorAndShowLibrary?.supportsBrightness;
        const supportsColors = Config.getLightColors(this.deviceConfig);
        


        const criticalFieldsPropertyAndValue = this.getCriticalFieldsPropertyAndValue() || [];
        return criticalFieldsPropertyAndValue.reduce((acc, curr) => (
            // Don't show features that are not supported
            (
                (curr.Identifier === criticalFieldIdentifiers.Twinkle && !supportsTwinkleSpeedAndDirection) ||
                (curr.Identifier === criticalFieldIdentifiers.Speed && !supportsSpeed) ||
                (curr.Identifier === criticalFieldIdentifiers.Brightness && !supportsBrightness) ||
                (curr.Identifier === criticalFieldIdentifiers.LightName && !supportsColors)
            ) ?
                acc :
                (
                    (acc ? (acc + " " + separator + " ") : "") +
                    (([ criticalFieldIdentifiers.Brightness, criticalFieldIdentifiers.Speed ] as string[]).includes(curr.Identifier) ?
                        // Show label for brightness and speed. 100% and 1x could be confusing.
                        curr.ActualPropertyAndValue.Property.DisplayNameShort + ':\xa0' :
                        // Exclude label for others e.g. power or color. The value itself explains it.
                        ''
                    ) +
                    curr.ActualPropertyAndValue.ValueFormatted
                )
        ), "");
    }

    getCriticalFields():PoolDeviceCriticalDesiredActual[] {
        let order = 0;
        const fields = [];
    
        {
            const DisplayName = Locale.gettext("Power");
            const common = {
                DisplayName, DisplayNameShort: DisplayName,
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.ONOFF,
                DisplayProcessingLogicUnitAsDisplayName: false,
            };
            fields.push({
                Identifier: criticalFieldIdentifiers.OnOff,
                // Controller dos not provide actual state
                ActualProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.PowerState }, common),
                DesiredProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.PowerState }, common)
            });
        }
        {
            const DisplayName = Locale.gettext("Light Name");
            const common = {
                DisplayName, DisplayNameShort: DisplayName,
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.LIGHT_NAME,
                DisplayProcessingLogicUnitAsDisplayName: false,
            };
            fields.push({
                Identifier: criticalFieldIdentifiers.LightName,
                // Controller dos not provide actual state
                ActualProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.LightName }, common),
                DesiredProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.LightName }, common)
            });
        }
        {
            const common = {
                DisplayName: Locale.gettext("Brightness"),
                DisplayNameShort: Locale.gettext("Brt"),
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.PERCENT,
                DisplayProcessingLogicUnitAsDisplayName: false,
            };
            fields.push({
                Identifier: criticalFieldIdentifiers.Brightness,
                // Controller dos not provide actual state
                ActualProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.Brightness }, common),
                DesiredProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.Brightness }, common)
            });
        }
        {
    
            const common = {
                DisplayName: Locale.gettext("Speed"),
                DisplayNameShort: Locale.gettext("Spd"),
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.X,
                DisplayProcessingLogicUnitAsDisplayName: false,
            };
            fields.push({
                Identifier: criticalFieldIdentifiers.Speed,
                // Controller does not provide actual state
                ActualProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.Speed }, common),
                DesiredProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.Speed }, common)
            });
        }
        {
            const DisplayName = Locale.gettext("Twinkle");
            const common = {
                DisplayName, DisplayNameShort: DisplayName,
                DisplayOrder: ++order,
                DisplayProcessingLogic: DisplayProcessingLogics.TWINKLE,
                DisplayProcessingLogicUnitAsDisplayName: false,
            };
            fields.push({
                Identifier: criticalFieldIdentifiers.Twinkle,
                // Controller does not provide actual state
                ActualProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.Twinkle }, common),
                DesiredProperty: Object.assign({ Name: PoolsideDaemonDeviceStateNames.Twinkle }, common)
            });
        }
    
        return fields;
    }    
}

export enum PoolsideDaemonDeviceStateNames {
    Brightness = "Brightness",
    LightName = "LightName",
    PowerState = "PowerState",
    Speed = "Speed",
    Twinkle = "Twinkle",
    TwinkleIncrements = "TwinkleIncrements",
}
